import dayjs from 'dayjs';

export const BATCH_URGENT_HOURS = 4;

export const BATCH_SOON_WITHIN_DAYS = 7;

export type BatchDueIconUrgency = 'critical' | 'soon' | 'ok' | 'neutral';

const ICON_ACCENT_CLASSES: Record<BatchDueIconUrgency, string> = {
    critical: 'bg-red-100 text-red-600',
    soon: 'bg-orange-100 text-orange-600',
    ok: 'bg-green-100 text-green-600',
    neutral: 'bg-slate-100 text-slate-500',
};

export interface BatchDueIconState {
    readonly urgency: BatchDueIconUrgency;
    readonly earliestOpenDueBy: string | null;
    readonly tooltipLabel: string;
}

type MinimalDueRequest = {
    readonly dueBy: string;
    readonly completed?: boolean | null;
};

export function getBatchDueIconAccentClasses(
    urgency: BatchDueIconUrgency,
): string {
    return ICON_ACCENT_CLASSES[urgency];
}

export const BATCH_URGENT_TOOLTIP_LABEL = `Urgent (Due within ${BATCH_URGENT_HOURS} hours or overdue)`;

export function getBatchDueIconState(
    requests: readonly MinimalDueRequest[],
): BatchDueIconState {
    const now = dayjs();
    const openRequests = requests.filter(r => !r.completed);

    if (openRequests.length === 0) {
        return {
            urgency: 'neutral',
            earliestOpenDueBy: null,
            tooltipLabel:
                'All requests in this batch are completed · no active due-by.',
        };
    }

    const earliestEntry = openRequests.reduce<{
        dueBy: string;
        ms: number;
    } | null>((acc, r) => {
        const d = dayjs(r.dueBy);
        if (!d.isValid()) {
            return acc;
        }
        const ms = d.valueOf();
        if (acc === null || ms < acc.ms) {
            return { dueBy: r.dueBy, ms };
        }
        return acc;
    }, null);

    const earliestDueBy = earliestEntry?.dueBy ?? null;

    if (earliestDueBy === null) {
        return {
            urgency: 'neutral',
            earliestOpenDueBy: null,
            tooltipLabel:
                'Open requests have no valid due-by date · zone urgency unavailable.',
        };
    }

    const minDue = dayjs(earliestDueBy);
    const urgentHorizonEnd = now.add(BATCH_URGENT_HOURS, 'hour');

    const isCritical = !minDue.isAfter(urgentHorizonEnd);

    let urgency: BatchDueIconUrgency;

    if (isCritical) {
        urgency = 'critical';
    } else if (minDue.isBefore(now.add(BATCH_SOON_WITHIN_DAYS, 'day'))) {
        urgency = 'soon';
    } else {
        urgency = 'ok';
    }

    const formatted = minDue.format('DD MMM YYYY HH:mm');
    const relative = minDue.fromNow();

    return {
        urgency,
        earliestOpenDueBy: earliestDueBy,
        tooltipLabel: `Earliest open request due · ${formatted} (${relative})`,
    };
}
